import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountUpdateInfo } from '../../api';
import { toast } from 'sonner';
import { useAuthStore } from '../../lib/auth';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    } else if (formData.newPassword.length > 100) {
      newErrors.newPassword = 'Password must be at most 100 characters long';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'The new password and confirmation password do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await accountUpdateInfo({
        requestBody: {
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        },
      });

      toast.success('Your password has been changed successfully. Please log in with your new password.');
      
      // Log out user after password change (matching Razor Pages behavior)
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to change password:', error);
      const e = error as { body?: { errors?: Record<string, unknown> }; message?: string };
      if (e.body?.errors) {
        const apiErrors: Record<string, string> = {};
        for (const [key, messages] of Object.entries(e.body.errors)) {
          apiErrors[key] = Array.isArray(messages) ? messages.join(', ') : String(messages);
        }
        setErrors(apiErrors);
      } else {
        toast.error(e.message || 'Failed to change password. Please check your current password and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Change Password</h1>
        <p className="mt-2 text-gray-600">Update your password to keep your account secure</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700">
              Current Password
            </label>
            <input
              type="password"
              id="oldPassword"
              value={formData.oldPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, oldPassword: e.target.value }))}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.oldPassword 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-smartfield focus:ring-smartfield'
              }`}
              required
              autoComplete="current-password"
            />
            {errors.oldPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.oldPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={formData.newPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.newPassword 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-smartfield focus:ring-smartfield'
              }`}
              required
              minLength={6}
              maxLength={100}
              autoComplete="new-password"
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Password must be at least 6 characters long.</p>
          </div>

          {/* Confirm New Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.confirmPassword 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-smartfield focus:ring-smartfield'
              }`}
              required
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/account/manage')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-smartfield text-white rounded-md hover:bg-smartfield/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Password Security Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Use a password that is at least 6 characters long</li>
                <li>Include a mix of letters, numbers, and symbols</li>
                <li>Avoid using common words or personal information</li>
                <li>You will be logged out after changing your password</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
